import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { fmtMoney, monthLabel, periodFor, shiftPeriod, useTankRentalEstimate, type EstimateRow } from '@/hooks/useTankRentals';

type Show = 'billed' | 'zero' | 'held' | 'all';

/**
 * What every enrolled customer WOULD be billed if the run went out right now
 * — the estimate behind the total on the roster. Nothing here writes: it is
 * the same compute and the same rules as Build, read live from today's counts,
 * so it moves as cylinders go out and come back. Default period is next
 * month, which is the invoice the current counts feed.
 */
export default function RentalsEstimate() {
  const [params, setParams] = useSearchParams();
  const param = params.get('period') ?? '';
  const period = /^\d{4}-\d{2}-01$/.test(param) ? param : null;
  const { data, isLoading, error } = useTankRentalEstimate(period);
  const [q, setQ] = useState('');
  const [show, setShow] = useState<Show>('billed');
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const shown = useMemo(() => {
    const all = data?.customers ?? [];
    const needle = q.trim().toLowerCase();
    return all.filter((c) => {
      if (show === 'billed' && !(c.status === 'draft' && c.total > 0)) return false;
      if (show === 'zero' && !(c.status === 'draft' && c.total === 0)) return false;
      if (show === 'held' && c.status !== 'held') return false;
      if (!needle) return true;
      return c.customer_name.toLowerCase().includes(needle) || c.qbo_customer_id.includes(needle);
    });
  }, [data, q, show]);

  const effective = data?.period ?? period ?? periodFor(new Date());
  const setPeriod = (p: string) => setParams({ period: p });
  const shownTotal = shown.reduce((s, c) => s + c.total, 0);

  return (
    <div>
      <Link to="/audit/rentals" className="inline-flex items-center gap-1 text-xs text-brix-muted hover:text-brix-ink"><ArrowLeft className="h-3.5 w-3.5" /> Program roster</Link>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-xl font-bold tracking-display-tight text-brix-ink">Estimated rentals — {monthLabel(effective)}</h1>
        <div className="flex items-center gap-1">
          <button type="button" className="rounded-md border border-brix-border p-1.5" onClick={() => setPeriod(shiftPeriod(effective, -1))} aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></button>
          <button type="button" className="rounded-md border border-brix-border p-1.5" onClick={() => setPeriod(shiftPeriod(effective, 1))} aria-label="Next month"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <p className="mt-1 text-xs text-brix-muted">
        What each enrolled customer would be billed if the run went out right now, from today’s cylinder counts. Nothing is written — the real invoices are built on the 1st and posted from <Link to="/audit/rentals/run" className="underline">the run page</Link>.
      </p>

      {isLoading ? (
        <div className="mt-6 flex items-center justify-center gap-2 py-16 text-brix-muted"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-sm">Computing every customer…</span></div>
      ) : error ? (
        <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">Couldn’t compute the estimate: {error.message}</div>
      ) : data ? (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-4">
            <Stat label="Estimated total" value={fmtMoney(data.total)} strong />
            <Stat label="Would be billed" value={String(data.billable)} sub="customers with cylinders out" />
            <Stat label="At $0" value={String(data.zero)} sub="nothing out, or reset line only" />
            <Stat label="Held" value={String(data.held)} sub="inactive in QuickBooks" warn={data.held > 0} />
          </div>
          <p className="mt-2 text-[11px] text-brix-muted">Computed {data.computed_at.slice(0, 16).replace('T', ' ')} UTC · {data.customers.length} enrolled.</p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {(['billed', 'zero', 'held', 'all'] as Show[]).map((f) => (
              <button key={f} type="button" onClick={() => setShow(f)} className={`rounded-full border px-3 py-1 text-xs font-medium ${show === f ? 'border-brix-primary bg-brix-primary text-white' : 'border-brix-border bg-brix-surface text-brix-ink'}`}>
                {f === 'billed' ? `Billed · ${data.billable}` : f === 'zero' ? `$0 · ${data.zero}` : f === 'held' ? `Held · ${data.held}` : `Everyone · ${data.customers.length}`}
              </button>
            ))}
            <div className="relative ml-auto min-w-[220px] flex-1 sm:flex-none">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brix-muted" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or QuickBooks #" className="pl-9" />
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-xl border border-brix-border bg-brix-surface shadow-card">
            {shown.length === 0 ? (
              <div className="p-6 text-center text-sm text-brix-muted">Nobody matches.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-brix-surface-alt text-left text-[11px] uppercase tracking-wide text-brix-muted">
                  <tr><th className="px-3 py-2">Customer</th><th className="px-3 py-2">Cylinders</th><th className="hidden px-3 py-2 md:table-cell">Invoice</th><th className="px-3 py-2 text-right">Estimate</th><th className="w-8" /></tr>
                </thead>
                <tbody>
                  {shown.map((c) => <Row key={c.qbo_customer_id} c={c} open={!!open[c.qbo_customer_id]} toggle={() => setOpen((s) => ({ ...s, [c.qbo_customer_id]: !s[c.qbo_customer_id] }))} />)}
                </tbody>
                <tfoot>
                  <tr className="border-t border-brix-border bg-brix-surface-alt font-semibold"><td colSpan={3} className="px-3 py-2 text-right">{shown.length} shown</td><td className="px-3 py-2 text-right">{fmtMoney(shownTotal)}</td><td /></tr>
                </tfoot>
              </table>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function Stat({ label, value, sub, strong, warn }: { label: string; value: string; sub?: string; strong?: boolean; warn?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 shadow-card ${warn ? 'border-amber-300 bg-amber-50' : 'border-brix-border bg-brix-surface'}`}>
      <div className="text-[11px] uppercase tracking-wide text-brix-muted">{label}</div>
      <div className={`mt-1 font-display font-bold text-brix-ink ${strong ? 'text-2xl' : 'text-xl'}`}>{value}</div>
      {sub && <div className="text-[11px] text-brix-muted">{sub}</div>}
    </div>
  );
}

function Row({ c, open, toggle }: { c: EstimateRow; open: boolean; toggle: () => void }) {
  const cyl = c.lines.filter((l) => l.qty > 0);
  return (
    <>
      <tr className="border-t border-brix-border">
        <td className="px-3 py-2">
          <Link to={`/audit/rentals/${c.qbo_customer_id}`} className="font-medium text-brix-ink hover:underline">{c.customer_name}</Link>
          <div className="text-[11px] text-brix-muted">
            QB #{c.qbo_customer_id}
            {c.status === 'held' && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-900">held</span>}
            {c.qbo_active === false && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-red-800">inactive in QuickBooks</span>}
          </div>
          {c.status !== 'draft' && c.reason && <div className="mt-0.5 text-[11px] text-amber-800">{c.reason}</div>}
        </td>
        <td className="px-3 py-2 text-xs text-brix-ink">
          {cyl.length === 0 ? <span className="text-brix-muted">—</span> : cyl.map((l) => (
            <div key={l.label}>
              {l.qty} × {l.display_name} @ {fmtMoney(l.unit_price)}
              {typeof l.audit_floor_from === 'number' && (
                <span className="ml-1 rounded bg-red-100 px-1 py-0.5 text-[10px] text-red-800" title={`Books say ${l.audit_floor_from}. Billed at the 4-tank minimum; stays on the audit list until a driver counts the site.`}>
                  count {l.audit_floor_from} · audit due
                </span>
              )}
            </div>
          ))}
        </td>
        <td className="hidden px-3 py-2 text-xs text-brix-muted md:table-cell">{c.doc_number}</td>
        <td className="px-3 py-2 text-right font-medium">{fmtMoney(c.total)}</td>
        <td className="px-2">
          {(c.lines.length > 0 || c.rows.length > 0) && (
            <button type="button" onClick={toggle} aria-label={open ? 'Hide lines' : 'Show lines'} className="rounded p-1 text-brix-muted hover:text-brix-ink">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-t border-brix-border bg-brix-surface-alt">
          <td colSpan={5} className="px-3 py-2">
            {c.warnings.map((w) => <p key={w} className="mb-1 text-xs text-amber-800">⚠ {w}</p>)}
            <table className="w-full text-xs">
              <thead className="text-left text-[10px] uppercase text-brix-muted"><tr><th className="py-1">Cylinder</th><th className="text-right">Prev</th><th className="text-right">+In</th><th className="text-right">−Out</th><th className="text-right">Now</th><th className="text-right">Rate</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {c.lines.map((l) => (
                  <tr key={l.label} className="border-t border-brix-border"><td className="py-1">{l.display_name}</td><td className="text-right">{l.prev}</td><td className="text-right">{l.deliveries}</td><td className="text-right">{l.pickups}</td><td className="text-right font-medium">{l.qty}</td><td className="text-right">{fmtMoney(l.unit_price)}</td><td className="text-right">{fmtMoney(l.amount)}</td></tr>
                ))}
                {c.rows.filter((r) => !c.lines.some((l) => l.label === r.label)).map((r) => (
                  <tr key={r.label} className="border-t border-brix-border text-brix-muted"><td className="py-1">{r.label}</td><td className="text-right">{r.prev_balance}</td><td className="text-right">{r.deliveries}</td><td className="text-right">{r.pickups}</td><td className={`text-right ${r.new_balance < 0 ? 'text-red-700' : ''}`}>{r.new_balance}</td><td className="text-right">—</td><td className="text-right">not billed</td></tr>
                ))}
              </tbody>
            </table>
            {c.rows[0]?.anchor_doc && <p className="mt-1 text-[11px] text-brix-muted">Counts anchor on {c.rows[0].anchor_doc} ({c.rows[0].anchor_date}).</p>}
          </td>
        </tr>
      )}
    </>
  );
}
